import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import "./styles/Portfolio.css";
import { getCache, setCache } from './utils/cache';
import { api } from "./utils/api";

import { ImageItem } from "./types";

const PortfolioGrid: React.FC = () => {

  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [headerImg, setHeaderImg] = useState<string>("");
  const navigate = useNavigate();


  const fetchHeaderImage = async () => {
    try {
      const cacheKey = `PortfolioHeaderImage`;
      const cachedHeader = getCache<string>(cacheKey);
      if (cachedHeader) {
        setHeaderImg(cachedHeader);
        return;
      }

      const data = await api.get(`/getImagesByCategory?category=cityscape&number=1`);
      const parsed = JSON.parse(await data.text()) as ImageItem[];
      console.log('Fetched header image:', parsed);

      setHeaderImg(parsed[0].fullImageUrl);
      setCache<string>(cacheKey, parsed[0].fullImageUrl, 15);

    } catch (error) {
      console.error('Error fetching header image:', error);
    }
  };

  const fetchThumbnails = async () => {
    for (const category of ['event', 'performance', 'portrait', 'cityscape', 'street', 'landscape']) {
      try {
        const cacheKey = `thumb:${category}`;
        const cachedThumb = getCache<Record<string, string>>(cacheKey);
        if (cachedThumb && cachedThumb.url) {
          setThumbs(prev => ({ ...prev, [category]: cachedThumb.url }));
          continue;
        }

        const data = await api.get(`/getImagesByCategory?category=${category}&count=1`);
        const parsed = JSON.parse(await data.text()) as ImageItem[];
        console.log('Fetched images:', category, parsed);

        const url = parsed?.[0]?.thumbnailUrl || '';
        setThumbs(prev => ({ ...prev, [category]: url }));
        // cache thumbnail url for 15 minutes
        setCache(cacheKey, { url }, 15);

      } catch (error) {
        console.error('Error fetching thumbnails:', error);
      }
    }
  };

  useEffect(() => {
    fetchHeaderImage();
    fetchThumbnails();
  }, []);

  function Hero() {
    return (
      <section className="dw-hero">
        <img
          src={headerImg}
          height={200}
          alt="Hero"
          className="dw-hero-img"
        />
        <div className="dw-hero-overlay" />
        <div className="dw-hero-content">
          <h1>David Wallach Photography</h1>
          <p>
            A collection of portrait, street, and other photography. Available for prints and commissions.
          </p>
        </div>
      </section>
    );
  }

  const CategoryGrid = useMemo(() => {

    const categories: { key: string; label: string; thumb: string }[] = [
      { key: 'portrait', label: 'Portrait', thumb: thumbs['portrait'] },
      { key: 'street', label: 'Street', thumb: thumbs['street'] },
      { key: 'performance', label: 'Performance', thumb: thumbs['performance'] },
      { key: 'event', label: 'Event', thumb: thumbs['event'] },
      { key: 'cityscape', label: 'CityScape', thumb: thumbs['cityscape'] },
      { key: 'landscape', label: 'Landscape', thumb: thumbs['landscape'] },
    ];

    return (
      <section className="dw-categories-section">
        <div className="dw-gallery-inner">
          <h2>Explore Categories</h2>
          <div className="dw-category-grid">
            {categories.map((c) => (
              <button
                key={c.key}
                className="dw-category-card"
                onClick={() => navigate(`/portfolio/${c.key}`)}
                aria-label={`Open ${c.label} gallery`}
              >
                <img src={c.thumb} alt={c.label} loading="lazy" />
                <div className="dw-category-label">{c.label}</div>
              </button>
            ))}
          </div>
        </div>
      </section>
    );
  }, [thumbs, navigate]);

  function ContactFooter() {
    return (
      <footer id="contact" className="dw-footer">
        <div className="dw-footer-inner">
          <div>
            <h4>Book a Session</h4>
            <p>
              Email: <a href="mailto:davidwallachphotography@gmail.com">davidwallachphotography@gmail.com</a>
            </p>
          </div>
          <div>
            <h4>Follow</h4>
            <div className="dw-footer-links">
              <a href="https://www.instagram.com/dawallofphotos/">Instagram</a>
            </div>
          </div>
        </div>
        <div className="dw-footer-copy">© {new Date().getFullYear()} David Wallach Photography</div>
      </footer>
    );
  }




  return (
    <div className="dw-wrapper">
      <Hero />
      {CategoryGrid}
      <ContactFooter />
    </div>
  );
}

export default PortfolioGrid;